// src/features/services/google-apis/api/sttApi.ts
// ────────────────────────────────────────────────────────────────────────────
// Google STT API client — typed BFF calls for Speech-to-Text.
//
// SRP: HTTP transport only.  UI logic lives in hooks, presentation in components.
// ────────────────────────────────────────────────────────────────────────────
import { bff } from '@/lib/http/bff';

// ── Request types ─────────────────────────────────────────────────────────

export type SttEncoding =
  | 'ENCODING_UNSPECIFIED'
  | 'LINEAR16'
  | 'FLAC'
  | 'MULAW'
  | 'AMR'
  | 'AMR_WB'
  | 'OGG_OPUS'
  | 'SPEEX_WITH_HEADER_BYTE'
  | 'MP3'
  | 'WEBM_OPUS';

export type SttModel =
  | 'default'
  | 'latest_long'
  | 'latest_short'
  | 'phone_call'
  | 'medical_dictation'
  | 'medical_conversation'
  | 'command_and_search'
  | 'video'
  | 'chirp'
  | 'telephony'
  | 'telephony_short';

export interface SttSpeechContext {
  phrases: string[];
  boost?: number;
}

export interface SttTranscribeRequest {
  /** Base64-encoded audio content (mutually exclusive with audioUri) */
  audioContent?: string;
  /** GCS URI for audio (mutually exclusive with audioContent) */
  audioUri?: string;
  /** BCP-47 language code (default: en-US) */
  languageCode?: string;
  /** Audio encoding format */
  encoding?: SttEncoding;
  /** Sample rate in Hz (default: 16000) */
  sampleRateHertz?: number;
  /** Number of audio channels (default: 1) */
  audioChannelCount?: number;
  /** Recognition model */
  model?: SttModel;
  /** Maximum number of recognition alternatives (1–30, default: 1) */
  maxAlternatives?: number;
  /** Enable automatic punctuation (default: true) */
  enableAutomaticPunctuation?: boolean;
  /** Enable word-level time offsets (default: false) */
  enableWordTimeOffsets?: boolean;
  /** Enable per-word confidence scores (default: false) */
  enableWordConfidence?: boolean;
  /** Filter profanity (default: false) */
  profanityFilter?: boolean;
  /** Use enhanced model (default: true) */
  useEnhanced?: boolean;
  /** Speech contexts for phrase hints */
  speechContexts?: SttSpeechContext[];
  /** Alternative language codes for multi-language detection */
  alternativeLanguageCodes?: string[];
}

// ── Response types ────────────────────────────────────────────────────────

export interface SttWordInfo {
  word: string;
  startTime: string;
  endTime: string;
  confidence: number;
  speakerTag: number;
}

export interface SttAlternative {
  transcript: string;
  confidence: number;
  words: SttWordInfo[];
  resultIndex: number;
  alternativeIndex: number;
  channelTag: number;
  languageCode: string;
}

export interface SttTranscribeResponse {
  ok: boolean;
  transcript: string;
  confidence: number;
  wordCount: number;
  alternatives: SttAlternative[];
  /** Word-level details from the top alternative (convenience accessor) */
  words?: SttWordInfo[];
  /** Detected language code when using multi-language detection */
  detectedLanguage?: string | null;
  resultCount: number;
  totalDuration: string | null;
  /** Orchestrator workflow_run_id */
  runId?: string | null;
  workflowKey?: string | null;
  stepName?: string | null;
  handlerKey?: string | null;
  gsUri?: string | null;
  browserUrl?: string | null;
  /** Present when using /upload endpoint */
  sourceFile?: {
    filename: string;
    mimetype: string;
    encoding: string;
    byteLength: number;
  };
}

export interface SttHealthResponse {
  ok: boolean;
  pipeline: string;
  credentials: {
    available: boolean;
    strategy: string;
    projectId: string;
  };
  scopes: string[];
}

// ── API functions ─────────────────────────────────────────────────────────

/**
 * Transcribe audio → text.  Accepts base64 audioContent or GCS audioUri.
 */
export function transcribeStt(body: SttTranscribeRequest): Promise<SttTranscribeResponse> {
  return bff.post<SttTranscribeResponse>('/google-apis/stt/transcribe', body);
}

/**
 * Transcribe audio file via multipart upload.
 * @param file - audio File object
 * @param params - optional recognition parameters
 */
export async function transcribeSttFile(
  file: File,
  params?: Partial<Pick<SttTranscribeRequest,
    'languageCode' | 'encoding' | 'model' | 'sampleRateHertz' | 'audioChannelCount' |
    'enableAutomaticPunctuation' | 'enableWordTimeOffsets' | 'enableWordConfidence' |
    'profanityFilter' | 'useEnhanced' | 'maxAlternatives' | 'alternativeLanguageCodes'
  >> & { durationHintSec?: number },
): Promise<SttTranscribeResponse> {
  const formData = new FormData();
  formData.append('file', file);

  const queryParts: string[] = [];
  if (params) {
    for (const [k, v] of Object.entries(params)) {
      if (v !== undefined) queryParts.push(`${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`);
    }
  }
  const qs = queryParts.length > 0 ? `?${queryParts.join('&')}` : '';

  // Use raw fetch for multipart — bff.post serializes as JSON
  const origin = window.location.origin;
  const jwt = localStorage.getItem('jwt') || '';

  const res = await fetch(`${origin}/bff/google-apis/stt/transcribe/upload${qs}`, {
    method: 'POST',
    headers: {
      ...(jwt ? { Authorization: `Bearer ${jwt}`, 'x-app-jwt': jwt } : {}),
    },
    body: formData,
  });

  if (!res.ok && res.status !== 202) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.detail || body.error || `STT upload failed: ${res.status}`);
  }

  const json = await res.json();
  json.__httpStatus = res.status;
  return json;
}

/**
 * Transcribe audio from a GCS URI via the /upload/complete endpoint.
 * This bypasses the workflow-agile path and calls the BFF service directly,
 * which has full encoding detection, sample-rate strategy, and LRO support.
 */
export function transcribeSttUri(
  gsUri: string,
  params?: Partial<Pick<SttTranscribeRequest,
    'languageCode' | 'encoding' | 'model' | 'sampleRateHertz' | 'audioChannelCount' |
    'enableAutomaticPunctuation' | 'enableWordTimeOffsets' | 'enableWordConfidence' |
    'profanityFilter' | 'useEnhanced' | 'maxAlternatives' | 'alternativeLanguageCodes'
  >>,
): Promise<SttTranscribeResponse> {
  return bff.post<SttTranscribeResponse>('/google-apis/stt/upload/complete', {
    gsUri,
    ...params,
  });
}

/**
 * STT pipeline health check.
 */
export function getSttHealth(): Promise<SttHealthResponse> {
  return bff.get<SttHealthResponse>('/google-apis/stt/health');
}
